import { CitationTag } from "components/CitationTag";
import { ButtonVariant, CoButton } from "components/CoButton";
import PaperMetadata from "components/PaperMetadata";
import SearchResultActions from "components/SearchResultActions";
import MeterTag from "components/Tag/tags/MeterTag";
import { FullPaperLibraryLink } from "features/libraries";
import { Paper } from "helpers/api";
import { detailPagePath } from "helpers/pageUrl";
import { SearchResultTextType } from "helpers/search";
import {
  MixpanelActionSource,
  MixpanelCiteType,
  MixpanelFullTextLinkType,
} from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import Link from "next/link";
import { YesNoType } from "types/YesNoType";

const CitationTooltipQuote = ({
  quote,
  onShowInPaperClick,
}: {
  quote: string;
  onShowInPaperClick: () => void;
}) => {
  const [labels] = useLabels("tooltips.citation");

  return (
    <div className="flex gap-3">
      <div className="w-[3px] rounded-full bg-border-base mx-2.5"></div>
      <div className="flex flex-wrap flex-1 gap-1">
        <span className="text-sm line-clamp-4 text-fg-base">{quote} </span>
        <button
          onClick={onShowInPaperClick}
          className="text-sm text-accent-fg-base"
        >
          {labels["show-in-paper"]}
        </button>
      </div>
    </div>
  );
};

type PropTypes = {
  paper?: Paper;
  onClick?: () => void;
  onShowInPaperClick?: () => void;
  isBookmarked?: boolean;
  text?: string;
  textType?: SearchResultTextType;
  absoluteLink: string;
  paperIndex: number;
  onActionTriggered: () => void;
  mixpanelActionSource?: MixpanelActionSource;
  meter?: YesNoType;
  hasUsedFullText?: boolean;
  quote?: string; // Quote text to display
  quoteIndex?: number; // Quote index within paper (0-based)
  sharePaperEventOverride?: (
    paper: Paper,
    paperIndex: number,
    shareType: string,
    resultType?: SearchResultTextType
  ) => void;
  onCitePaper?: (
    paper: Paper,
    citeType: MixpanelCiteType,
    paperIndex: number,
    resultType?: SearchResultTextType
  ) => void;
  onFullTextClick?: (
    paper: Paper,
    linkType: MixpanelFullTextLinkType,
    actionSource: MixpanelActionSource,
    paperIndex?: number,
    libraryId?: string
  ) => void;
};
export const CitationTooltip = ({
  paper,
  onClick,
  onShowInPaperClick,
  text,
  textType,
  absoluteLink,
  paperIndex,
  onActionTriggered,
  mixpanelActionSource,
  meter,
  hasUsedFullText,
  quote,
  quoteIndex,
  sharePaperEventOverride,
  onCitePaper,
  onFullTextClick,
}: PropTypes) => {
  const [labels] = useLabels("tooltips.citation");
  if (!paper) {
    return null;
  }

  const handleShowInPaper = () => {
    onActionTriggered(); // Hide the tooltip
    onShowInPaperClick?.(); // Open the sidebar
  };

  const handleMiddleClick = (e: React.MouseEvent) => {
    // Fire onClick if middle mouse button is clicked
    if (e.button === 1 && onClick) {
      onClick();
    }
  };

  let link = detailPagePath(paper.url_slug, paper.paper_id);

  // Hide the "Used Full Text" badge if a quote is present to render the quote instead.
  const showUsedFullTextTag = hasUsedFullText && !quote;

  return (
    <div>
      <section className="flex flex-col gap-3">
        {meter ? (
          <div className="flex gap-2 items-center">
            <CitationTag
              paperIndex={paperIndex}
              quoteIndex={quoteIndex}
              variant={meter}
              showQuoteIcon={!!quote}
            />
            <Link
              href={link}
              target="_blank"
              className="flex flex-1 mr-2 not-prose"
              onClick={onClick}
              onMouseDown={handleMiddleClick}
            >
              <h3 className="base-md line-clamp-2 text-fg-base">
                {paper.title}
              </h3>
            </Link>
            <MeterTag meter={meter} />
          </div>
        ) : (
          <div className="flex gap-2 items-center">
            <CitationTag
              paperIndex={paperIndex}
              quoteIndex={quoteIndex}
              showQuoteIcon={!!quote}
            />
            <Link
              href={link}
              target="_blank"
              className="flex flex-1 mr-2 not-prose"
              onClick={onClick}
              onMouseDown={handleMiddleClick}
            >
              <h3 className="base-md line-clamp-2 text-fg-base">
                {paper.title}
              </h3>
            </Link>
          </div>
        )}
        {quote && onShowInPaperClick && (
          <CitationTooltipQuote
            quote={quote}
            onShowInPaperClick={handleShowInPaper}
          />
        )}

        <PaperMetadata
          paper={paper}
          showUsedFullTextTag={showUsedFullTextTag}
          useTwoRowLayout
        />
        <div className="flex justify-between pt-3 border-t">
          <div className="flex gap-2 items-center">
            <Link
              href={absoluteLink}
              onClick={onClick}
              onMouseDown={handleMiddleClick}
            >
              <CoButton
                variant={ButtonVariant.Secondary}
                label={labels["details"]}
                icon="icon-info"
                size="sm"
              />
            </Link>
            <FullPaperLibraryLink
              item={paper}
              variant={ButtonVariant.Tertiary}
              actionSource={mixpanelActionSource}
              index={paperIndex}
              onFullTextClick={
                onFullTextClick
                  ? (item, linkType, actionSource, idx, libraryId) => {
                      // Cast item back to Paper since we know it's a Paper in this context
                      onFullTextClick(
                        item as Paper,
                        linkType,
                        actionSource,
                        idx,
                        libraryId
                      );
                    }
                  : undefined
              }
            />
          </div>
          <SearchResultActions
            shareText={text}
            shareTextType={textType}
            shareLink={absoluteLink}
            item={paper}
            searchPage={0} //TODO(Jimmy): Pass Page to search result events here
            paperIndex={paperIndex}
            onActionTriggered={onActionTriggered}
            mixpanelActionSource={mixpanelActionSource}
            noSocialSharing
            sharePaperEventOverride={sharePaperEventOverride}
            onCitePaper={onCitePaper}
            hideFullPaperLibraryLink
          />
        </div>
      </section>
    </div>
  );
};
